/**
 * API Client
 * 用於發送需要認證的 API 請求
 *
 * 注意：認證通過 HttpOnly Cookie 自動處理，無需手動帶 Authorization header
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * 處理 401 未授權錯誤
 * 清除本地認證狀態並重新載入頁面
 */
function handle401Error(): void {
  // 清除 localStorage 中的用戶資料
  localStorage.removeItem('auth_user');
  // 重新載入頁面，觸發重新登入
  window.location.href = '/';
}

/**
 * 發送需要認證的 API 請求
 * Cookie 會由瀏覽器自動帶上
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    credentials: 'include', // 自動帶上 HttpOnly Cookie
    headers: {
      'Content-Type': 'application/json',
      // CSRF 防護：後端會擋掉沒有這個 header 的變更資料請求
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers,
    },
  });

  // 處理 401 未授權錯誤
  if (response.status === 401) {
    handle401Error();
    throw new Error('未授權，請重新登入');
  }

  return response;
}

/**
 * 處理大數字精度問題
 * 將 JSON 中的大數字（如 Snowflake ID）轉換為字串，避免精度丟失
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSON 解析結果型別由呼叫端的泛型 T 決定，此處為型別邊界，無法收斂
function parseBigIntJSON(text: string): any {
  // 正則表達式匹配 "tripId": 後面的大數字，將其包裹在引號中
  const fixedText = text.replace(/"tripId"\s*:\s*(\d+)/g, '"tripId":"$1"');
  return JSON.parse(fixedText);
}

/**
 * GET 請求
 */
export async function apiGet<T>(url: string): Promise<T> {
  const response = await authenticatedFetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`API 請求失敗: ${response.status}`);
  }

  // 使用自定義解析器處理大數字
  const text = await response.text();
  return parseBigIntJSON(text);
}

/**
 * POST 請求
 */
export async function apiPost<T>(url: string, data?: unknown): Promise<T> {
  const response = await authenticatedFetch(url, {
    method: 'POST',
    ...(data != null ? { body: JSON.stringify(data) } : {}),
  });

  if (!response.ok) {
    throw new Error(`API 請求失敗: ${response.status}`);
  }

  const text = await response.text();
  return parseBigIntJSON(text);
}

/**
 * PUT 請求
 */
export async function apiPut<T>(url: string, data: unknown): Promise<T> {
  const response = await authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`API 請求失敗: ${response.status}`);
  }

  const text = await response.text();
  return parseBigIntJSON(text);
}

/**
 * PATCH 請求
 */
export async function apiPatch<T>(url: string, data: unknown): Promise<T> {
  const response = await authenticatedFetch(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`API 請求失敗: ${response.status}`);
  }

  const text = await response.text();
  return parseBigIntJSON(text);
}

/**
 * DELETE 請求
 */
export async function apiDelete<T>(url: string): Promise<T> {
  const response = await authenticatedFetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`API 請求失敗: ${response.status}`);
  }

  const text = await response.text();
  return parseBigIntJSON(text);
}
